<template>
  <List
    class="campaign-list"
    titleI18nKey="admin.crm.title.campaign.list"
    :loading="loading"
    :listData="listData"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :pagination="pagination"
    @create="handleCreate"
    @pagination-change="fetchListData"
  >
    <template #name="{ row, data }">
      <router-link class="link" :to="{ name: 'editCrmCampaign', params: { campaignId: row.id } }">{{ data }}</router-link>
    </template>
    <template #filter>
      <div class="filter-keywords">
        <el-input
          v-model="search.keywords"
          clearable
          :placeholder="$t('admin.crm.placeholder.keywords')"
          @change="fetchListData"
          @keypress.enter.native="fetchListData"
        >
          <template #prefix>
            <i class="el-input__icon el-icon-search"></i>
          </template>
        </el-input>
      </div>
      <div class="filter-micro-site">
        <el-select
          v-model="search.microsite"
          clearable
          filterable
          popper-class="microsite-select"
          :placeholder="$t('admin.crm.placeholder.campaign.search.microsite')"
          @change="fetchListData"
        >
          <el-option
            v-for="(item, key) in micrositeList"
            :key="key"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </template>
    <template #page-bottom>
      <QrCodeDialog v-bind="qrCodeDialog" :show.sync="qrCodeDialog.show" />
      <ExportDialog v-bind="exportDialog" :show.sync="exportDialog.show"/>
    </template>
  </List>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import QrCodeDialog from '@/components/dialog/qrCode.vue'
import ExportDialog from './exportDialog.vue'
import List from '@/components/tableList/allowDragSortTable.vue'
import { ROOT_COMPANY_ID } from '@/constant/company'

export default {
  components: {
    List,
    QrCodeDialog,
    ExportDialog
  },
  metaInfo () {
    return {
      style: [
        { innerHTML: '.el-select-dropdown.el-popper.microsite-select{max-width: 372px;}' }
      ]
    }
  },
  data () {
    return {
      loadingCount: 0,
      listData: [],
      actionBtn: {
        copy: {
          labelI18nKey: 'admin.crm.btn.campaign.copyURL',
          onclick: this.handleCopyLink
        },
        qrCode: {
          labelI18nKey: 'admin.crm.btn.campaign.qrCode',
          onclick: this.handleOpenQrCode
        },
        send: {
          labelI18nKey: 'admin.crm.btn.campaign.send',
          onclick: this.handleSendNotification
        },
        report: {
          labelI18nKey: 'admin.crm.btn.campaign.report',
          onclick: this.handleOpenReport
        },
        export: {
          show: (rowData) => rowData.microsite.has_form,
          labelI18nKey: 'admin.crm.btn.campaign.export',
          onclick: this.handelExport
        }
      },
      pagination: {
        total: 1,
        page: 1,
        pageSize: 10
      },
      companyInfo: {},
      logoFormatter: null,
      logoFormatterFinished: false,
      micrositeList: [],
      search: {
        keywords: null,
        microsite: null
      },
      qrCodeDialog: {
        show: false,
        headerLabel: '',
        qrImageName: '',
        qrLogo: '',
        qrLogoScale: 0.23,
        qrData: '',
        qrSize: 500
      },
      exportDialog: {
        show: false,
        siteId: '',
        campaignName: '',
        micrositeName: ''
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    loading: {
      get () {
        return this.loadingCount > 0
      },
      set (val) {
        this.loadingCount += val ? 1 : -1
      }
    },
    tableColumns () {
      const columns = [
        {
          labelI18nKey: 'admin.crm.label.campaign.name',
          dataKey: 'name',
          slotName: 'name'
        },
        {
          labelI18nKey: 'admin.crm.label.microsite.micrositeName',
          dataKey: 'microsite.name'
        },
        {
          labelI18nKey: 'admin.crm.label.pv',
          dataKey: 'pv',
          formatNumber: true
        },
        {
          labelI18nKey: 'admin.crm.label.application',
          dataKey: 'applied',
          formatNumber: true
        }
      ]
      return columns
    }
  },
  created () {
    this.loading = true
    Promise.all([this.initMicrositeData(), this.fetchListData()]).finally(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions('crm', ['searchMicrosite', 'searchCampaign']),
    async initMicrositeData () {
      const resp = await this.searchMicrosite({ is_published: true })
      if (resp) {
        this.micrositeList = resp
      }
    },
    async fetchListData () {
      this.loading = true
      try {
        const resp = await this.searchCampaign({
          keywords: this.search.keywords,
          microsite: this.search.microsite,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        if (resp) {
          if (resp) {
            this.companyInfo = resp.company
            this.qrCodeDialog.qrLogo = this.companyInfo.logo
            let origin = window.location.origin
            if (this.getCurrentAccount.root_company_id === ROOT_COMPANY_ID.MERCEDES_BENZ) {
              origin = process.env.VUE_APP_MERCEDES_BENZ_URL
            }
            const url = `${origin}/recruiting/crm/campaign/`
            this.pagination.total = resp.campaign.total
            this.listData = _.map(resp.campaign.data, item => {
              return {
                ...item,
                link: `${url}${item.short_unique_id}`
              }
            })
          }
        }
      } finally {
        this.loading = false
      }
    },
    handleCreate () {
      this.$router.push({ name: 'addCrmCampaign' })
    },
    handleCopyLink (rowData) {
      prompt(this.$t('admin.crm.label.campaign.copyLink'), rowData.link)
    },
    handleOpenQrCode (rowData) {
      const qrCodeDialog = this.qrCodeDialog
      qrCodeDialog.show = true
      qrCodeDialog.qrData = rowData.link
      qrCodeDialog.headerLabel = rowData.name
      qrCodeDialog.qrImageName = rowData.name
    },
    handleSendNotification (rowData) {
      this.$router.push({ name: 'sendCrmCampaign', params: { campaignId: rowData.id } })
    },
    handleOpenReport (rowData) {
      this.$router.push({ path: '/v-admin/crm-management/report', query: { campaign: rowData.id, microsite: rowData.microsite.id } })
    },
    handelExport (rowData) {
      const exportDialog = this.exportDialog
      exportDialog.show = true
      exportDialog.siteId = rowData.short_unique_id
      exportDialog.campaignName = rowData.name
      exportDialog.micrositeName = rowData.microsite.name
    }
  }
}
</script>

<style lang="scss" scoped>
.microsite-select {
  .el-select-dropdown__item {
    padding: 0 10px;
  }
}
</style>
